<template>
  <section class="section-padding container-padding">
    <div class="p-8 bg-white rounded-md border max-w-[800px] mx-auto">
      <h1 class="w-full text-center mb-2 text-gray-900">
        Municipality Selection
      </h1>

      <p class="w-full text-center mb-8">
        For which municipality are you capturing data?
      </p>

      <div class="border rounded-md">
        <template v-if="activeMunicipalities?.length > 0">
          <button
            v-for="municipality in activeMunicipalities"
            :key="municipality.id"
            class="block w-full text-center p-2 cursor-pointer hover:bg-secondary-subtle"
            :class="{ 'bg-secondary-subtle font-semibold': municipality.current }"
            aria-current="true"
            @click="handleChangeMunicipality(municipality)"
            @mouseover="hoveredMunicipality = municipality.id"
            @mouseleave="hoveredMunicipality = null"
          >
            <span class="w-fit relative">
              <span class="pr-2">{{ municipality.name }}</span>
              <span class="municipality-label">
                <transition>
                  <span
                    v-show="hoveredMunicipality === municipality.id && hoveredMunicipality !== municipality.current"
                    class="absolute right-0 translate-x-full rounded border border-secondary bg-secondary whitespace-nowrap text-white px-1 text-xs font-medium pt-[1px]"
                  >
                    Switch to this Municipality
                  </span>
                </transition>
              </span>
            </span>
          </button>
        </template>

        <div
          v-else
          class="text-center p-5"
        >
          There are no active municipalities assigned to user
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { useHttp } from '@/composables';
import { useAuthStore, useToastStore } from '@/store';

const authStore = useAuthStore();
const toastStore = useToastStore();
const router = useRouter();
const loading = ref(false);
const loadingMunicipalityId = ref(null);
const hoveredMunicipality = ref(null);
const activeMunicipalities = ref(null);

const loadMunicipalities = async () => {
  loading.value = true;

  const response = await useHttp().get('/profile');

  if (response.statusCode === HttpStatusCode.Ok) {
    authStore.user.municipalities = response.data.municipalities;
    // Only display the active municipalities on the selection but if they navigate to the plan for an archived municipality there will be a warning
    activeMunicipalities.value = authStore.user.municipalities.filter(municipality => !municipality.archived);
  }

  loading.value = false;
};

onMounted(async () => {
  await loadMunicipalities();
});

const handleChangeMunicipality = async (municipality) => {
  loading.value = true;
  loadingMunicipalityId.value = municipality.id;

  const response = await useHttp().put('/profile/municipality', { currentMunicipality: municipality.id });

  if (response.statusCode === HttpStatusCode.Ok) {
    authStore.user = response.data;

    const currentMunicipality = authStore.user.municipalities.find(municipality => municipality.current);

    await router.push({ name: 'app_plan_overview', params: { municipality: currentMunicipality.id } });
  } else {
    toastStore.type = 'danger';
    toastStore.message = response.exception;
  }

  loading.value = false;
  loadingMunicipalityId.value = null;
};

</script>

<style scoped>
/* we will explain what these classes do next! */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

@media (max-width: 992px) {
  .municipality-label {
    display: none;
  }
}
</style>
